import React from "react";
import { motion } from "framer-motion";
import { Sparkles, MessageCircle, Copy, Send, ArrowRight, Bell, Newspaper, CalendarDays, Tag } from "lucide-react";

const EXAMPLES = [
  "Che notifica invio questa settimana?",
  "Scrivimi una promo per i saldi estivi",
  "Idea di evento per la riapertura a settembre",
  "Un testo news per il nuovo arrivo in negozio",
];

const STEPS = [
  { icon: MessageCircle, title: "1. Chiedi ad Asia", text: "Apri l'assistente in basso a destra e scrivi cosa ti serve, come parlassi a un collega esperto di marketing." },
  { icon: Sparkles, title: "2. Asia scrive il testo", text: "In pochi secondi ricevi un messaggio pronto, breve ed efficace, adatto alla tua attività e all'obiettivo." },
  { icon: Copy, title: "3. Copia e incolla", text: "Copia il testo e incollalo nella tua web app per inviare la notifica o pubblicare news, evento o promozione." },
];

export const AsiaSection = () => {
  return (
    <section id="asia" className="relative overflow-hidden py-20 sm:py-28 bg-[#F9FAFB]" data-testid="asia-section">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute -top-20 right-0 h-80 w-80 rounded-full bg-brand-light/40 blur-3xl" />
      </div>

      <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <span className="inline-flex items-center gap-2 rounded-full bg-brand-light/40 px-4 py-1.5 text-sm font-bold uppercase tracking-[0.15em] text-brand">
            <Sparkles size={15} strokeWidth={2.5} /> Assistente AI
          </span>
          <h2 className="mt-5 font-heading text-3xl sm:text-4xl font-bold tracking-tight text-brand-deep leading-tight">
            Asia scrive le tue notifiche.<span className="block text-brand">Tu le invii con un copia-incolla.</span>
          </h2>
          <p className="mt-4 font-body text-base sm:text-lg text-ink leading-relaxed">
            <b className="text-brand-deep">Asia</b> è l'assistente PushApp che trovi sempre in basso a destra, in ogni pagina del sito.
            Non sai cosa scrivere o quando inviare? Chiediglielo: ti suggerisce il messaggio giusto e ti dà idee su misura per la tua attività.
          </p>

          <div className="mt-6">
            <p className="font-body text-sm font-semibold text-brand-deep mb-3">Cosa puoi chiederle, ad esempio:</p>
            <div className="flex flex-wrap gap-2">
              {EXAMPLES.map((e) => (
                <span key={e} className="rounded-full border border-brand/25 bg-white px-3.5 py-1.5 text-sm font-medium text-brand">“{e}”</span>
              ))}
            </div>
          </div>

          <div className="mt-8 space-y-4">
            {STEPS.map((s) => (
              <div key={s.title} className="flex items-start gap-4">
                <span className="flex h-11 w-11 flex-none items-center justify-center rounded-2xl bg-white text-brand shadow-sm border border-gray-100">
                  <s.icon size={20} strokeWidth={2.5} />
                </span>
                <div>
                  <p className="font-heading font-semibold text-brand-deep">{s.title}</p>
                  <p className="font-body text-ink text-[15px] leading-relaxed">{s.text}</p>
                </div>
              </div>
            ))}
          </div>

          <button
            onClick={() => window.dispatchEvent(new Event("open-asia"))}
            data-testid="asia-section-cta"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-brand px-8 py-4 font-body font-semibold text-white shadow-lg shadow-brand/30 transition-all duration-300 hover:bg-brand-hover hover:-translate-y-1"
          >
            <MessageCircle size={18} strokeWidth={2.5} /> Chiedi ad Asia
          </button>

          <div className="mt-5 flex flex-wrap gap-4 text-sm text-ink/70">
            <span className="inline-flex items-center gap-1.5"><Bell size={15} className="text-brand" /> Notifiche</span>
            <span className="inline-flex items-center gap-1.5"><Newspaper size={15} className="text-brand" /> News</span>
            <span className="inline-flex items-center gap-1.5"><CalendarDays size={15} className="text-brand" /> Eventi</span>
            <span className="inline-flex items-center gap-1.5"><Tag size={15} className="text-brand" /> Promozioni</span>
          </div>
        </div>

        {/* Chat mockup */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="rounded-[2rem] bg-white border border-gray-100 shadow-xl shadow-brand/5 overflow-hidden"
        >
          <div className="bg-gradient-to-br from-brand to-brand-green px-5 py-4 flex items-center gap-3 text-white">
            <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-white/20"><Sparkles size={20} strokeWidth={2.5} /></span>
            <div>
              <p className="font-heading font-bold leading-tight">Asia · Assistente PushApp</p>
              <p className="text-xs text-white/80 flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-white ring-2 ring-white/40" /> Online ora</p>
            </div>
          </div>
          <div className="p-5 space-y-3 bg-[#F9FAFB]">
            <div className="flex justify-end">
              <div className="max-w-[85%] rounded-2xl rounded-br-md bg-brand px-4 py-2.5 font-body text-sm text-white">
                Scrivimi una notifica per i saldi di questo weekend
              </div>
            </div>
            <div className="flex justify-start">
              <div className="max-w-[88%] rounded-2xl rounded-bl-md bg-white border border-gray-100 px-4 py-3 font-body text-sm text-ink shadow-sm">
                Ecco un testo pronto 👇<br /><br />
                <span className="block rounded-xl bg-brand-light/20 p-3 text-brand-deep">“🔥 Saldi al via! Solo per gli iscritti: -30% su tutto, questo weekend. Ti aspettiamo in negozio!”</span>
                <span className="mt-2 block text-xs text-ink/70">Copialo e incollalo nella tua web app per inviarlo.</span>
              </div>
            </div>
            <div className="flex justify-start">
              <button
                onClick={() => window.dispatchEvent(new Event("open-asia"))}
                className="inline-flex items-center gap-1.5 rounded-full bg-white border border-brand/30 px-3.5 py-1.5 text-xs font-semibold text-brand hover:bg-brand hover:text-white transition-colors"
              >
                <Copy size={13} strokeWidth={2.5} /> Prova ora con Asia
              </button>
            </div>
          </div>
          <div className="border-t border-gray-100 p-3 flex items-center gap-2 bg-white">
            <div className="flex-1 rounded-full bg-[#F9FAFB] border border-gray-200 px-4 py-2.5 font-body text-sm text-ink/50">Scrivi un messaggio...</div>
            <span className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-brand text-white"><Send size={17} strokeWidth={2.5} /></span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
